/**
 * External meeting URL parser — frontend mirror of
 * src/shared/externalMeetingUrlParser.ts.
 *
 * Returns the same shape as the backend so the UI and the API agree about
 * whether a pasted URL is a recognized Zoom or Teams meeting. Used by
 * ExternalMeetingSetupPage to drive provider-aware UI (hide password +
 * meeting-number fields for Teams; surface lobby-admission copy).
 *
 * Keep this file in sync with the backend version. Any pattern added there
 * must be added here too.
 */

const TEAMS_BUSINESS_HOST = 'teams.microsoft.com';
const TEAMS_BUSINESS_PATH_PREFIX = '/l/meetup-join/';
// Newer "Teams Meet ID" format (2024+): teams.microsoft.com/meet/<id>?p=<passcode>.
// Business host + shorter /meet/ path. Recall joins these the same way.
const TEAMS_BUSINESS_MEET_PATH_PREFIX = '/meet/';
const TEAMS_PERSONAL_HOST = 'teams.live.com';
const TEAMS_PERSONAL_PATH_PREFIX = '/meet/';
const ZOOM_PATH_REGEX = /^\/(j|wc|s)\/(\d+)/;

// Google Meet — mirror src/shared/externalMeetingUrlParser.ts
const MEET_HOST = 'meet.google.com';
const MEET_CANONICAL_PATH_REGEX = /^\/([a-z]{3}-[a-z]{4}-[a-z]{3})\/?$/;
const MEET_LOOKUP_PATH_REGEX = /^\/lookup\/([A-Za-z0-9._-]+)\/?$/;

// Webex — any *.webex.com host; accept /meet/<id> + other join forms.
const WEBEX_HOST_SUFFIX = '.webex.com';
const WEBEX_MEET_PATH_REGEX = /^\/meet\/([A-Za-z0-9._-]+)\/?$/;

function isZoomHost(host) {
  const h = String(host || '').toLowerCase();
  return (
    h === 'zoom.us' ||
    h === 'zoom.com' ||
    h.endsWith('.zoom.us') ||
    h.endsWith('.zoom.com')
  );
}

function isTeamsBusinessHost(host) {
  return String(host || '').toLowerCase() === TEAMS_BUSINESS_HOST;
}

function isTeamsPersonalHost(host) {
  return String(host || '').toLowerCase() === TEAMS_PERSONAL_HOST;
}

function isGoogleMeetHost(host) {
  return String(host || '').toLowerCase() === MEET_HOST;
}

function isWebexHost(host) {
  const h = String(host || '').toLowerCase();
  return h === 'webex.com' || h.endsWith(WEBEX_HOST_SUFFIX);
}

/**
 * Parse a pasted URL. Never throws.
 *
 * Returns either:
 *   { valid: true, provider: 'zoom'|'teams', meetingNumber: string|null,
 *     embeddedPwd: string|null }
 * or
 *   { valid: false, reason: string }
 */
export function parseExternalMeetingUrl(raw) {
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return { valid: false, reason: 'URL is required' };
  }
  let u;
  try {
    u = new URL(raw.trim());
  } catch {
    return { valid: false, reason: 'Invalid URL format' };
  }
  if (u.protocol !== 'https:' && u.protocol !== 'http:') {
    return { valid: false, reason: 'URL must use http or https' };
  }
  const host = u.hostname;

  if (isZoomHost(host)) {
    const match = u.pathname.match(ZOOM_PATH_REGEX);
    if (!match) {
      return {
        valid: false,
        reason: 'Could not find a Zoom meeting number in the URL',
      };
    }
    const meetingNumber = match[2];
    // Recent Zoom URLs put `pwd` in the URL fragment (#pwd=...); fall back to
    // query string for older link formats. Without the hash check,
    // password-protected Zoom meetings would join without a password.
    const hashMatch = u.hash.match(/[#&]pwd=([^&]+)/);
    const embeddedPwd =
      (hashMatch && hashMatch[1] ? decodeURIComponent(hashMatch[1]) : null) ||
      u.searchParams.get('pwd') ||
      u.searchParams.get('passcode') ||
      null;
    return {
      valid: true,
      provider: 'zoom',
      meetingNumber,
      embeddedPwd,
    };
  }

  if (isTeamsBusinessHost(host)) {
    const isLegacyJoinLink = u.pathname.startsWith(TEAMS_BUSINESS_PATH_PREFIX);
    const isMeetIdLink = u.pathname.startsWith(TEAMS_BUSINESS_MEET_PATH_PREFIX);
    if (!isLegacyJoinLink && !isMeetIdLink) {
      return {
        valid: false,
        reason:
          'Teams URL must look like teams.microsoft.com/l/meetup-join/... or teams.microsoft.com/meet/... (the Join link from the calendar invite)',
      };
    }
    return {
      valid: true,
      provider: 'teams',
      meetingNumber: null,
      embeddedPwd: null,
    };
  }

  if (isTeamsPersonalHost(host)) {
    if (!u.pathname.startsWith(TEAMS_PERSONAL_PATH_PREFIX)) {
      return {
        valid: false,
        reason:
          'Personal Teams URL must look like teams.live.com/meet/... (the Join link from the meeting)',
      };
    }
    return {
      valid: true,
      provider: 'teams',
      meetingNumber: null,
      embeddedPwd: null,
    };
  }

  if (isGoogleMeetHost(host)) {
    const canonicalMatch = u.pathname.match(MEET_CANONICAL_PATH_REGEX);
    if (canonicalMatch) {
      return {
        valid: true,
        provider: 'meet',
        meetingNumber: canonicalMatch[1] || null,
        embeddedPwd: null,
      };
    }
    const lookupMatch = u.pathname.match(MEET_LOOKUP_PATH_REGEX);
    if (lookupMatch) {
      return {
        valid: true,
        provider: 'meet',
        meetingNumber: lookupMatch[1] || null,
        embeddedPwd: null,
      };
    }
    return {
      valid: false,
      reason:
        'Google Meet URL must look like meet.google.com/xxx-yyyy-zzz (the meeting code from the calendar invite)',
    };
  }

  if (isWebexHost(host)) {
    const meetMatch = u.pathname.match(WEBEX_MEET_PATH_REGEX);
    if (meetMatch) {
      return {
        valid: true,
        provider: 'webex',
        meetingNumber: meetMatch[1] || null,
        embeddedPwd: u.searchParams.get('MTID') || null,
      };
    }
    if (u.pathname.length > 1) {
      return {
        valid: true,
        provider: 'webex',
        meetingNumber: null,
        embeddedPwd: u.searchParams.get('MTID') || null,
      };
    }
    return {
      valid: false,
      reason: 'Webex URL must include a meeting path (e.g. yourco.webex.com/meet/<id>)',
    };
  }

  return {
    valid: false,
    reason: 'Only Zoom, Microsoft Teams, Google Meet, and Webex URLs are supported',
  };
}
